import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import paycheckService from "../../api/paycheckService";

export const ACTION_FLAGS = {
  SAVE_PAYCHECK: "paycheck/savePaycheck",
  LIST_PAYCHECKS: "paycheck/listPaychecks",
  UPDATE_PAYCHECK: "paycheck/updatePaycheck",
  REMOVE_PAYCHECK: "paycheck/removePaycheck",
  DUPLICATE_PAYCHECK: "paycheck/duplicatePaycheck",
  VIEW_PAYCHECK: "paycheck/viewPaycheck",
  CALCULATE_PAYCHECK: "paycheck/calculatePaycheck",
};

export const savePaycheck = createAsyncThunk(
  ACTION_FLAGS.SAVE_PAYCHECK,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await paycheckService.create(payload);
      return {
        savedPaycheck: response?.data?.data || response?.data || {},
        message: response?.data?.message || "Paycheck saved successfully!",
        actionFlag: "SAVE_PAYCHECK_SCS",
      };
    } catch (error) {
      const body = error.response?.data || {};
      return rejectWithValue({
        message: body.message || error.message || "Failed to save paycheck",
        errorType: body.errorType || "",
        status: error.response?.status || 0,
        data: body.data || {},
        actionFlag: "SAVE_PAYCHECK_ERR",
      });
    }
  },
);

export const listPaychecks = createAsyncThunk(
  ACTION_FLAGS.LIST_PAYCHECKS,
  async (params = {}, { rejectWithValue }) => {
    const { append = false, ...query } = params;
    try {
      const response = await paycheckService.list(query);
      const body = response?.data ?? {};
      const data = body?.data ?? {};
      const rawList =
        data?.scenarios ||
        data?.paycheck_scenarios ||
        data?.rows ||
        data?.items ||
        (Array.isArray(data) ? data : []);
      const list = Array.isArray(rawList) ? rawList : [];
      return {
        paycheckList: list,
        append,
        page: Number(query?.page) || 1,
        limit: Number(query?.limit) || list.length,
        // `total` is the paginated visible total (capped at plan_limit for
        // free users). Use it for hasMore math so free-cap users don't
        // keep firing empty page requests.
        total: data?.total ?? data?.count ?? list.length,
        // `total_saved` reflects every row in the DB regardless of plan cap —
        // for the "you have X hidden, upgrade to see more" copy.
        totalSaved: data?.total_saved ?? data?.total ?? list.length,
        planSlug: data?.plan_slug ?? "",
        planLimit: data?.plan_limit ?? null,
        overPlanCount: data?.over_plan_count ?? 0,
        actionFlag: "LIST_PAYCHECKS_SCS",
      };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load paychecks";
      return rejectWithValue({
        message,
        actionFlag: "LIST_PAYCHECKS_ERR",
      });
    }
  },
);

export const updatePaycheck = createAsyncThunk(
  ACTION_FLAGS.UPDATE_PAYCHECK,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await paycheckService.update(id, data);
      return {
        updatedPaycheck: response?.data?.data || response?.data || {},
        message: response?.data?.message || "Paycheck updated successfully!",
        actionFlag: "UPDATE_PAYCHECK_SCS",
      };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to update paycheck";
      return rejectWithValue({
        message,
        actionFlag: "UPDATE_PAYCHECK_ERR",
      });
    }
  },
);

export const calculatePaycheck = createAsyncThunk(
  ACTION_FLAGS.CALCULATE_PAYCHECK,
  async (payload, { rejectWithValue, signal }) => {
    try {
      const response = await paycheckService.calculate(payload, { signal });
      return {
        calculation: response?.data?.data || response?.data || {},
        actionFlag: "CALCULATE_PAYCHECK_SCS",
      };
    } catch (error) {
      const body = error.response?.data || {};
      return rejectWithValue({
        message:
          body.message || error.message || "Failed to calculate paycheck",
        errorType: body.errorType || "",
        status: error.response?.status || 0,
        data: body.data || {},
        actionFlag: "CALCULATE_PAYCHECK_ERR",
      });
    }
  },
);

export const viewPaycheck = createAsyncThunk(
  ACTION_FLAGS.VIEW_PAYCHECK,
  async (id, { rejectWithValue }) => {
    try {
      const response = await paycheckService.touchView(id);
      return {
        viewedId: id,
        viewedAt:
          // Backend wraps the row under `data.scenario` (see touchView ctrl).
          response?.data?.data?.scenario?.last_viewed_at ||
          response?.data?.data?.last_viewed_at ||
          response?.data?.last_viewed_at ||
          new Date().toISOString(),
        actionFlag: "VIEW_PAYCHECK_SCS",
      };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to mark paycheck as viewed";
      return rejectWithValue({
        message,
        actionFlag: "VIEW_PAYCHECK_ERR",
      });
    }
  },
);

export const removePaycheck = createAsyncThunk(
  ACTION_FLAGS.REMOVE_PAYCHECK,
  async (id, { rejectWithValue }) => {
    try {
      const response = await paycheckService.remove(id);
      return {
        removedId: id,
        message: response?.data?.message || "Paycheck removed successfully!",
        actionFlag: "REMOVE_PAYCHECK_SCS",
      };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to remove paycheck";
      return rejectWithValue({
        message,
        actionFlag: "REMOVE_PAYCHECK_ERR",
      });
    }
  },
);

export const duplicatePaycheck = createAsyncThunk(
  ACTION_FLAGS.DUPLICATE_PAYCHECK,
  async (id, { rejectWithValue }) => {
    try {
      const response = await paycheckService.duplicate(id);
      return {
        savedPaycheck: response?.data?.data || response?.data || {},
        message: response?.data?.message || "Paycheck duplicated",
        actionFlag: "DUPLICATE_PAYCHECK_SCS",
      };
    } catch (error) {
      const body = error.response?.data || {};
      return rejectWithValue({
        message:
          body.message || error.message || "Failed to duplicate paycheck",
        errorType: body.errorType || "",
        status: error.response?.status || 0,
        data: body.data || {},
        actionFlag: "DUPLICATE_PAYCHECK_ERR",
      });
    }
  },
);

const initialState = {
  savedPaycheck: {},
  paycheckList: [],
  paycheckTotal: 0,
  paycheckTotalSaved: 0,
  paycheckPage: 0,
  paycheckHasMore: false,
  planSlug: "",
  planLimit: null,
  overPlanCount: 0,
  calculation: null,
  isLoading: false,
  isListLoading: false,
  isCalculating: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
  successMessage: "",
  actionFlag: "",
};

const paycheckSlice = createSlice({
  name: "paycheck",
  initialState,
  reducers: {
    resetPaycheckState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
      state.successMessage = "";
      state.actionFlag = "";
    },
    clearMessages: (state) => {
      state.successMessage = "";
      state.errorMessage = "";
      state.actionFlag = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(savePaycheck.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
        state.successMessage = "";
      })
      .addCase(savePaycheck.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.savedPaycheck = action.payload.savedPaycheck || {};
        state.successMessage = action.payload.message;
        state.actionFlag = action.payload.actionFlag;
        state.errorMessage = "";
      })
      .addCase(savePaycheck.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to save paycheck";
        state.actionFlag = action.payload?.actionFlag || "SAVE_PAYCHECK_ERR";
        state.successMessage = "";
      })
      .addCase(listPaychecks.pending, (state) => {
        state.isListLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(listPaychecks.fulfilled, (state, action) => {
        state.isListLoading = false;
        state.isSuccess = true;
        const incoming = action.payload.paycheckList || [];
        if (action.payload.append) {
          const existingIds = new Set(state.paycheckList.map((p) => p?.id));
          const fresh = incoming.filter((p) => !existingIds.has(p?.id));
          state.paycheckList = state.paycheckList.concat(fresh);
        } else {
          state.paycheckList = incoming;
        }
        state.paycheckTotal = action.payload.total ?? state.paycheckList.length;
        state.paycheckTotalSaved =
          action.payload.totalSaved ?? state.paycheckTotal;
        state.paycheckPage = action.payload.page || 1;
        state.paycheckHasMore = state.paycheckList.length < state.paycheckTotal;
        state.planSlug = action.payload.planSlug ?? "";
        state.planLimit = action.payload.planLimit ?? null;
        state.overPlanCount = action.payload.overPlanCount ?? 0;
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(listPaychecks.rejected, (state, action) => {
        state.isListLoading = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to load paychecks";
        state.actionFlag = action.payload?.actionFlag || "LIST_PAYCHECKS_ERR";
      })
      .addCase(updatePaycheck.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(updatePaycheck.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.savedPaycheck = action.payload.updatedPaycheck || {};
        state.successMessage = action.payload.message;
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(updatePaycheck.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to update paycheck";
        state.actionFlag = action.payload?.actionFlag || "UPDATE_PAYCHECK_ERR";
      })
      .addCase(calculatePaycheck.pending, (state) => {
        state.isCalculating = true;
        state.isError = false;
      })
      .addCase(calculatePaycheck.fulfilled, (state, action) => {
        state.isCalculating = false;
        state.calculation = action.payload.calculation || {};
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(calculatePaycheck.rejected, (state, action) => {
        state.isCalculating = false;
        if (action.error?.name !== "AbortError") {
          state.isError = true;
          state.errorMessage =
            action.payload?.message || "Failed to calculate paycheck";
        }
        state.actionFlag =
          action.payload?.actionFlag || "CALCULATE_PAYCHECK_ERR";
      })
      .addCase(viewPaycheck.fulfilled, (state, action) => {
        const { viewedId, viewedAt } = action.payload;
        state.paycheckList = state.paycheckList.map((p) =>
          p?.id === viewedId ? { ...p, last_viewed_at: viewedAt } : p,
        );
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(viewPaycheck.rejected, (state, action) => {
        state.actionFlag = action.payload?.actionFlag || "VIEW_PAYCHECK_ERR";
      })
      .addCase(removePaycheck.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(removePaycheck.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.paycheckList = state.paycheckList.filter(
          (p) => p?.id !== action.payload.removedId,
        );
        state.successMessage = action.payload.message;
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(removePaycheck.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to remove paycheck";
        state.actionFlag = action.payload?.actionFlag || "REMOVE_PAYCHECK_ERR";
      });
  },
});

export const { resetPaycheckState, clearMessages } = paycheckSlice.actions;
export default paycheckSlice.reducer;
